import React, { useEffect, useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Loader2 } from 'lucide-react';
interface RegistrationPopupProps {
  isOpen: boolean;
  onClose: () => void;
}
export function RegistrationPopup({
  isOpen,
  onClose
}: RegistrationPopupProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const firstFieldRef = useRef<HTMLInputElement>(null);
  // ============================================================================
  // CONFIGURACIÓN DE REDIRECCIÓN Y CRM
  // ============================================================================
  // 1. URL a la que se redirige al usuario después de registrarse exitosamente.
  //    REEMPLAZAR por tu página de gracias / acceso al curso / video de bienvenida.
  const REDIRECT_URL = 'https://tudominio.com/gracias-o-acceso-curso';
  // 2. Función Stub para conectar con GetResponse o ActiveCampaign.
  //    Por defecto NO hace llamadas reales (simula éxito). Cuando tengas tus
  //    credenciales, descomentá el bloque del CRM que uses y pegá tus datos.
  const submitLead = async (leadData: {
    name: string;
    email: string;
  }) => {
    /* ------------------------------------------------------------------
       OPCIÓN A — GETRESPONSE
       Docs: https://apidocs.getresponse.com/v3/resources/contacts
       --------------------------------------------------------------------
       const GR_API_KEY = 'PEGÁ_TU_API_KEY_ACÁ';
       const GR_CAMPAIGN_ID = 'PEGÁ_TU_CAMPAIGN_(LIST)_ID_ACÁ';
       const GR_ENDPOINT = 'https://api.getresponse.com/v3/contacts';
            const res = await fetch(GR_ENDPOINT, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
           'X-Auth-Token': `api-key ${GR_API_KEY}`,
         },
         body: JSON.stringify({
           name: leadData.name,
           email: leadData.email,
           campaign: { campaignId: GR_CAMPAIGN_ID },
         }),
       });
       if (!res.ok) throw new Error('Error al registrar en GetResponse');
       return;
       ------------------------------------------------------------------ */
    /* ------------------------------------------------------------------
       OPCIÓN B — ACTIVECAMPAIGN
       Docs: https://developers.activecampaign.com/reference/create-a-contact
       --------------------------------------------------------------------
       const AC_API_URL = 'https://TU-CUENTA.api-us1.com'; // base de tu cuenta
       const AC_API_KEY = 'PEGÁ_TU_API_KEY_ACÁ';
       const AC_LIST_ID = 'PEGÁ_TU_LIST_ID_ACÁ';
            // 1) Crear/actualizar el contacto
       const res = await fetch(`${AC_API_URL}/api/3/contacts`, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
           'Api-Token': AC_API_KEY,
         },
         body: JSON.stringify({
           contact: { email: leadData.email, firstName: leadData.name },
         }),
       });
       if (!res.ok) throw new Error('Error al registrar en ActiveCampaign');
       const { contact } = await res.json();
            // 2) Suscribir el contacto a la lista
       await fetch(`${AC_API_URL}/api/3/contactLists`, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
           'Api-Token': AC_API_KEY,
         },
         body: JSON.stringify({
           contactList: { list: AC_LIST_ID, contact: contact.id, status: 1 },
         }),
       });
       return;
       ------------------------------------------------------------------ */
    // Simulación de llamada a la API (ELIMINAR cuando conectes tu CRM real).
    console.log('[submitLead] Lead capturado (modo demo):', leadData);
    return new Promise<void>(resolve => setTimeout(resolve, 1500));
  };
  const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim()) {
      setError('Por favor, completá todos los campos.');
      return;
    }
    if (!validateEmail(email)) {
      setError('Ingresá un correo electrónico válido.');
      return;
    }
    setIsSubmitting(true);
    try {
      await submitLead({
        name: name.trim(),
        email: email.trim()
      });
      // Redirección tras éxito
      window.location.href = REDIRECT_URL;
    } catch (err) {
      console.error(err);
      setError('Hubo un problema al procesar tu registro. Intentá nuevamente.');
      setIsSubmitting(false);
    }
  };
  // Cerrar con Escape + bloquear el scroll del body mientras está abierto
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    // Enfocar el primer campo al abrir
    const t = setTimeout(() => firstFieldRef.current?.focus(), 50);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
      clearTimeout(t);
    };
  }, [isOpen, isSubmitting, onClose]);
  return <AnimatePresence data-id="element-89">
      {isOpen && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} onMouseDown={e => {
      // Cerrar al hacer click en el backdrop (no en el contenido)
      if (e.target === e.currentTarget && !isSubmitting) onClose();
    }} data-id="element-90">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="registration-title" className="relative w-full max-w-md p-8 bg-brand-dark border border-gray-800 rounded-2xl shadow-2xl" initial={{
        opacity: 0,
        scale: 0.95,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 16
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 26
      }} data-id="element-91">
            <button type="button" onClick={onClose} disabled={isSubmitting} aria-label="Cerrar" className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange rounded disabled:opacity-50" data-id="element-92">
              <X className="w-6 h-6" data-id="element-93" />
            </button>

            <div className="text-center mb-6" data-id="element-94">
              <h3 id="registration-title" className="text-2xl font-bold text-white mb-2" data-id="element-95">
                Estás a un paso de acceder
              </h3>
              <p className="text-gray-400 text-sm" data-id="element-96">
                Ingresá tus datos para enviarte el acceso inmediato al curso
                gratuito.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" noValidate data-id="element-97">
              <div data-id="element-98">
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-99">
                  Tu Nombre
                </label>
                <input ref={firstFieldRef} type="text" id="name" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent text-white placeholder-gray-500 transition-all" placeholder="Ej: Juan Pérez" disabled={isSubmitting} autoComplete="name" data-id="element-100" />
              </div>

              <div data-id="element-101">
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-102">
                  Tu Mejor Correo
                </label>
                <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent text-white placeholder-gray-500 transition-all" placeholder="ejemplo@correo.com" disabled={isSubmitting} autoComplete="email" data-id="element-103" />
              </div>

              {error && <p role="alert" className="text-red-500 text-sm text-center" data-id="element-104">
                  {error}
                </p>}

              <button type="submit" disabled={isSubmitting} className="w-full py-4 px-6 mt-4 bg-brand-orange hover:bg-red-600 text-white font-bold rounded-lg transition-all transform hover:scale-[1.02] active:scale-95 flex items-center justify-center box-glow disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-dark focus-visible:ring-brand-orange" data-id="element-105">
                {isSubmitting ? <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-106" />
                    Procesando...
                  </> : '¡ACCEDER AL CURSO GRATIS!'}
              </button>

              <p className="text-xs text-center text-gray-500 mt-4" data-id="element-107">
                Tus datos están 100% seguros. Cero Spam.
              </p>
            </form>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}